import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { PaymentMethodDiscount } from "../../../types/admin";

interface PaymentMethodDiscountTableProps {
  discounts: PaymentMethodDiscount[];
  loading?: boolean;
  onEdit: (discount: PaymentMethodDiscount) => void;
  onDelete: (discount: PaymentMethodDiscount) => void;
  onView: (discount: PaymentMethodDiscount) => void;
}

const PaymentMethodDiscountTable: React.FC<PaymentMethodDiscountTableProps> = ({
  discounts,
  loading,
  onEdit,
  onDelete,
  onView,
}) => {
  const { t } = useTranslation();
  const formatDiscountValue = (discount: PaymentMethodDiscount) => {
    if (discount.discountType === 1) {
      // Percentage
      return `${discount.discountValue}%`;
    } else {
      // FixedAmount
      return <Price value={discount.discountValue} disableCentsHeuristic />;
    }
  };

  const isExpired = (discount: PaymentMethodDiscount) => {
    if (!discount.endDate) return false;
    return new Date(discount.endDate) < new Date();
  };

  const hasReachedMaxUses = (discount: PaymentMethodDiscount) => {
    if (!discount.maxTotalUses) return false;
    return discount.currentTotalUses >= discount.maxTotalUses;
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.paymentMethod")}</TableCell>
              <TableCell>{t("admin.description")}</TableCell>
              <TableCell>{t("admin.discount")}</TableCell>
              <TableCell>{t("admin.feeType")}</TableCell>
              <TableCell>{t("admin.status")}</TableCell>
              <TableCell>{t("admin.uses")}</TableCell>
              <TableCell>{t("admin.minOrder")}</TableCell>
              <TableCell>{t("admin.expires")}</TableCell>
              <TableCell align="right">{t("admin.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : discounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.noPaymentMethodDiscountsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              discounts.map((discount) => {
                const expired = isExpired(discount);
                const maxUsesReached = hasReachedMaxUses(discount);

                return (
                  <TableRow key={discount.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {discount.paymentMethodTypeDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={discount.description}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {discount.description}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDiscountValue(discount)}</TableCell>
                    <TableCell>{discount.feeTypeDisplay}</TableCell>
                    <TableCell>
                      <Chip
                        label={discount.isActive ? t("admin.active") : t("admin.inactive")}
                        color={discount.isActive ? "success" : "default"}
                        size="small"
                      />
                      {expired && (
                        <Chip
                          label={t("admin.expired")}
                          color="error"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {maxUsesReached && (
                        <Chip
                          label={t("admin.maxUsesReached")}
                          color="warning"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {discount.maxTotalUses
                        ? `${discount.currentTotalUses} / ${discount.maxTotalUses}`
                        : `${discount.currentTotalUses} (${t("admin.unlimited")})`}
                    </TableCell>
                    <TableCell>
                      {discount.minOrderValue ? (
                        <Price value={discount.minOrderValue} disableCentsHeuristic />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {discount.endDate
                        ? new Date(discount.endDate).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("admin.viewDetails")}>
                        <IconButton
                          size="small"
                          onClick={() => onView(discount)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("admin.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(discount)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("admin.delete")}>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(discount)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PaymentMethodDiscountTable;

